// 2026-09-24 - FormuLearning - Step 6 Dissolution profiles — Reference Lot·Test 배치 선택 + 시점 편집 (이슈 #63)
// 파일 위치: frontend/src/features/step6/DissolutionSection.tsx
// 2026-11-03 수정 - 이슈 #114: viewer 는 Reference·Test 선택·시점 추가/삭제 비활성(Reference/Test 탭은 열어 둔다).
// 설계: 개정안 §3.2 #11·12(M915-17: reference = Step 3 대조약 Lot, test = 배치 선택) · BR-S6-05
// 배치를 바꾸는 것은 «수정» 이 아니라 선택이다 — 선택 시 그 배치 용출을 연동값 스냅샷으로 함께 둔다.

import { useEffect, useState } from 'react'
import type { BatchCandidates, DissBlock, PkInputs, ProfileRow, Step6View } from '../../api/step6'
import { getBatchCandidates } from '../../api/step6'
import { editedPaths, isEdited, mediumName, reload, revert, setPath } from './pkModel'
import { GroupHead, NumField, inputCls } from './fields'
import { useCanWrite, READ_ONLY_TITLE } from '../../hooks/useCanWrite'

interface Props {
  projectId: string
  view: Step6View
  inputs: PkInputs
  onChange: (next: PkInputs) => void
}

type Form = 'ref' | 'test'

export default function DissolutionSection({ projectId, view, inputs, onChange }: Props) {
  const [cand, setCand] = useState<BatchCandidates | null>(null)
  const [form, setForm] = useState<Form>('ref')
  const canWrite = useCanWrite()
  useEffect(() => { getBatchCandidates(projectId).then(setCand).catch(() => setCand({ reference: [], test: [] })) }, [projectId])

  const paths = view.link_groups.dissolution ?? ['dissolution.ref', 'dissolution.test']
  const edited = editedPaths(inputs, paths)
  const stale = view.stale.filter((p) => paths.includes(p))
  const block: DissBlock = inputs.dissolution[form]
  const path = `dissolution.${form}`

  const choose = (f: Form, block: DissBlock, source: string) => {
    const next = setPath(inputs, `dissolution.${f}`, block)
    onChange({ ...next, linked: { ...(next.linked ?? {}), [`dissolution.${f}`]: { value: structuredClone(block), source } } })
  }
  const setProfiles = (profiles: ProfileRow[]) => onChange(setPath(inputs, path, { ...(block ?? {}), profiles }))
  const setPoint = (pi: number, ri: number, ci: 0 | 1, v: number | null) =>
    setProfiles(block!.profiles.map((p, k) => (k !== pi ? p : {
      ...p, points: p.points.map((r, j) => (j !== ri ? r : (ci === 0 ? [v ?? NaN, r[1]] : [r[0], v]) as [number, number | null])),
    })))

  return (
    <section className="mb-4 rounded border p-3" style={{ borderColor: 'var(--border-subtle)', backgroundColor: 'var(--color-bg-panel)' }}>
      <GroupHead
        title="Dissolution profiles"
        sources={paths.map((p) => view.linked[p]?.source).filter(Boolean) as string[]}
        editedCount={edited.length}
        onRevert={() => onChange(revert(inputs, edited))}
        stale={stale.length > 0}
        onReload={() => onChange(reload(inputs, stale, view.linked))}
      />
      <p className="mb-2 text-[10px] text-text-muted">
        PK 예측 신뢰도를 높이려면 위 · 소장 상부 · 소장 하부의 생리 환경을 모사하는 용출시험액을 선정해야 합니다.
        pH 로 해석되지 않는 용출액(물 등)은 계산에서 빠지고, 0 분 점이 없으면 (0, 0)을 앞에 붙입니다.
      </p>
      <div className="mb-2 grid gap-2 sm:grid-cols-2">
        <label className="block">
          <span className="mb-0.5 block text-[10px] text-text-secondary">Reference — Step 3 대조약 Lot</span>
          <select className={inputCls} value={inputs.dissolution.ref?.lot_id ?? ''} disabled={!canWrite} title={canWrite ? undefined : READ_ONLY_TITLE}
            onChange={(e) => {
              const c = cand?.reference.find((x) => x.lot_id === e.target.value)
              if (c) choose('ref', { lot_id: c.lot_id, label: c.label, profiles: c.profiles }, `Step 3 대조약 ${c.label} 용출`)
            }}>
            {!inputs.dissolution.ref && <option value="">— 대조약 용출 없음 —</option>}
            {cand?.reference.map((c) => <option key={c.lot_id} value={c.lot_id}>{c.label}{c.is_active ? ' (선택 Lot)' : ''}</option>)}
          </select>
        </label>
        <label className="block">
          <span className="mb-0.5 block text-[10px] text-text-secondary">Test — 배치 선택 (기본: Step 4 최종 배치)</span>
          <select className={inputCls} value={inputs.dissolution.test?.batch_id ?? ''} disabled={!canWrite} title={canWrite ? undefined : READ_ONLY_TITLE}
            onChange={(e) => {
              const c = cand?.test.find((x) => x.batch_id === e.target.value)
              if (c) choose('test', { batch_id: c.batch_id, label: c.batch_code, profiles: c.profiles },
                `배치 ${c.batch_code} 용출${c.is_final ? ' (Step 4 최종 배치)' : ''}`)
            }}>
            {!inputs.dissolution.test && <option value="">— 배치를 선택하세요 —</option>}
            {cand?.test.map((c) => (
              <option key={c.batch_id} value={c.batch_id}>
                {c.batch_code}{c.round_no != null ? ` · R${c.round_no}` : ''}{c.is_final ? ' (최종)' : ''}
              </option>
            ))}
          </select>
        </label>
      </div>

      <div className="mb-2 flex gap-1" role="tablist">
        {(['ref', 'test'] as Form[]).map((f) => (
          <button key={f} type="button" role="tab" aria-selected={form === f} onClick={() => setForm(f)}
            className="rounded px-3 py-1 text-[11px]"
            style={form === f ? { backgroundColor: 'var(--bg-chip-teal)', color: 'var(--color-accent-teal)' } : { color: 'var(--color-text-secondary)' }}>
            {f === 'ref' ? 'Reference' : 'Test'}{isEdited(inputs, `dissolution.${f}`) ? ' •' : ''}
          </button>
        ))}
      </div>

      {!block?.profiles?.length ? (
        <p className="text-[11px] text-text-secondary">{form === 'ref' ? 'Step 3 에 대조약 용출이 없습니다.' : 'Test 배치를 선택하세요.'}</p>
      ) : (
        <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
          {block.profiles.map((p, pi) => (
            <div key={p.ph} className="rounded border" style={{ borderColor: 'var(--border-soft)' }}>
              <div className="border-b px-2 py-1 text-[11px] font-medium text-text-body" style={{ borderColor: 'var(--border-soft)' }}>
                pH {p.ph} {mediumName(p.ph) && <span className="text-text-muted">· {mediumName(p.ph)}</span>}
              </div>
              <div className="max-h-56 overflow-y-auto">
                <table className="w-full text-[11px]">
                  <thead><tr className="text-[10px] text-text-secondary"><th className="px-1 py-0.5 font-normal">Time (min)</th><th className="px-1 py-0.5 font-normal">Dissolution (%)</th><th /></tr></thead>
                  <tbody>
                    {p.points.map((r, ri) => (
                      <tr key={ri}>
                        <td className="px-1 py-0.5"><NumField align="center" value={Number.isFinite(r[0]) ? r[0] : null} onCommit={(v) => setPoint(pi, ri, 0, v)} /></td>
                        <td className="px-1 py-0.5"><NumField align="center" value={r[1]} onCommit={(v) => setPoint(pi, ri, 1, v)} /></td>
                        <td className="px-1">
                          <button type="button" aria-label="행 삭제" disabled={!canWrite} title={canWrite ? undefined : READ_ONLY_TITLE} className="text-text-muted hover:text-text-body disabled:cursor-not-allowed disabled:opacity-40"
                            onClick={() => setProfiles(block.profiles.map((x, k) => (k !== pi ? x : { ...x, points: x.points.filter((_, j) => j !== ri) })))}>×</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <button type="button" disabled={!canWrite} title={canWrite ? undefined : READ_ONLY_TITLE} className="w-full border-t py-1 text-[10px] text-text-secondary hover:text-text-body disabled:cursor-not-allowed disabled:opacity-40" style={{ borderColor: 'var(--border-soft)' }}
                onClick={() => {
                  const last = p.points.length ? p.points[p.points.length - 1][0] : 0
                  setProfiles(block.profiles.map((x, k) => (k !== pi ? x : { ...x, points: [...x.points, [last + 30, null]] })))
                }}>+ 시점 추가</button>
            </div>
          ))}
        </div>
      )}
    </section>
  )
}
